import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleFlag } from '@/components/ui/CircleFlag'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
} from '@/components/ui/Combobox'
import { controlShellClass, type ControlSize } from '@/components/ui/control'
import { cn } from '@/lib/cn'
import {
  findCountry,
  joinPhone,
  phoneOptions,
  splitPhone,
  type Country,
} from '@/lib/countries'

/* The phone field: a country calling code, then the number.
 *
 * It replaces a bare `<input type="tel">`, which is how one mobile ends up in
 * the directory three times as "+7 701 …", "8 701 …" and "701 …". The country is
 * now a choice with a flag on it, and the code it contributes is written into
 * the stored string rather than remembered by the typist.
 *
 * ── One string, not two fields ──
 *
 * `contact.phone` is a single free-text column and stays one. This control
 * splits it for editing and re-joins it on every keystroke: "+7 701 234 56 78"
 * goes to the server exactly as before, so nothing downstream — the directory
 * list, the debit note, an export — has to learn about a second field, and a row
 * saved before this control existed still loads.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: validate, format, or normalise. That is
 * libphonenumber's job, it costs ~150 KB, and a brokerage's address book
 * legitimately holds "+7 701 234 56 78 (ext. 12)" and "ask for Dmitry". Only the
 * dial code is structured — it is the part people get wrong — and everything
 * after it is passed through untouched.
 *
 * ── Reading a stored number back ──
 *
 * `splitPhone` recognises a leading "+code" and nothing else. "8 701 …" is a
 * national form, "701 …" a local one, and guessing a country for either would
 * silently rewrite somebody's address book on the first edit; both keep the flag
 * slot empty (a globe) and sit in the number field exactly as typed.
 *
 * Shared codes are the other half of it: +7 is Russia AND Kazakhstan, +1 is a
 * dozen countries. The stored digits cannot say which, so `splitPhone` answers
 * with the main territory (KZ for +7 here — this is a Kazakh brokerage) and the
 * user's own pick wins over that for as long as the code still matches. Picking
 * Russia over Kazakhstan changes the flag and nothing about the number, which is
 * exactly right: the two are the same +7.
 */

export interface PhoneInputProps {
  /** The whole stored phone string, e.g. "+7 701 234 56 78". */
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  disabled?: boolean
  placeholder?: string
  size?: ControlSize
  className?: string
  /** Which flag an empty field opens on. The local country, by default. */
  defaultCountry?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/* Base UI: composed from Combobox.tsx for the code picker; the number itself is
   a plain `<input>`, because it is one. */
export function PhoneInput({
  value,
  onChange,
  id,
  name,
  disabled,
  placeholder,
  size = 'md',
  className,
  defaultCountry = 'KZ',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: PhoneInputProps) {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const parsed = splitPhone(value)

  /* The country the USER pointed at, which the stored digits cannot record.
     Null until they touch the picker, so a fresh field follows the value. */
  const [pickedCode, setPickedCode] = useState<string | null>(null)
  const picked = findCountry(pickedCode)

  /* The pick wins while it is still consistent with what is stored — same dial
     code, or nothing recognisable stored at all. It loses the moment the value
     changes to a different code underneath it (a paste, a different contact
     loaded into the same sheet), because then the field is showing a number the
     pick no longer describes. */
  const country: Country | undefined =
    picked && (!parsed.country || picked.dial === parsed.country.dial)
      ? picked
      : (parsed.country ?? (value.trim() === '' ? findCountry(defaultCountry) : undefined))

  const options = phoneOptions(language)
  const byCode = new Map(options.map((option) => [option.value, option]))

  /* Typing that starts with "+" is a whole number being pasted or retyped —
     including its own country code — so it is stored as-is and re-parsed on the
     next render, which moves the flag. Anything else is the national part and
     gets the selected country's code put back in front of it. */
  const handleNumber = (next: string) => {
    if (!next.trimStart().startsWith('+')) {
      onChange(joinPhone(country, next))
      return
    }
    onChange(next)
    /* The typed code becomes the pick, so the flag survives the next keystroke.
       Without this, a space typed straight after "+375" empties the stored
       string — joinPhone has nothing to prefix — and the country would fall
       back to the default with the rest of the number appended to it. Only a
       RECOGNISED code counts: "+3" is half of one and must not clear the pick. */
    const typed = splitPhone(next).country
    if (typed) setPickedCode(typed.code)
  }

  return (
    <Combobox
      items={options.map((option) => option.value)}
      value={country?.code ?? null}
      onValueChange={(next: string | null) => {
        setPickedCode(next)
        const chosen = findCountry(next)
        /* Re-stamps the stored string with the new code. `joinPhone` returns
           empty for an empty number, so choosing a country in a blank field
           stores nothing — "+7" alone in a directory reads as a phone number
           that does not dial. */
        onChange(joinPhone(chosen, parsed.national))
      }}
      itemToStringLabel={(code: string) => byCode.get(code)?.label ?? code}
      filter={(code: string, query: string) => {
        const needle = query.trim().toLowerCase()
        if (needle === '') return true
        return byCode.get(code)?.search.includes(needle) ?? false
      }}
      disabled={disabled}
      autoHighlight
    >
      {/* ONE box, two controls: the shell is drawn here so the code button and
          the number read as a single field, and `focus-within` lights the whole
          box whichever half has focus. */}
      <div
        className={cn(
          controlShellClass(size),
          'flex items-center gap-2 py-0 pr-3 pl-2.5',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
        aria-invalid={invalid}
      >
        <ComboboxTrigger
          bare
          disabled={disabled}
          // The button's own text is "+7", which names no country; the label
          // says what the control is, and the flag's title says which country
          // is currently on it.
          aria-label={t('common.form.phoneCode')}
          className="disabled:cursor-not-allowed"
        >
          <CircleFlag code={country?.code} size={18} />
          <span className="text-text-body shrink-0 text-[13px]">
            {country ? `+${country.dial}` : ''}
          </span>
        </ComboboxTrigger>

        {/* The hairline between the two halves. Decorative: the label on the
            button and the label on the field already separate them for a screen
            reader. */}
        <span aria-hidden className="bg-border-subtle h-4 w-px shrink-0" />

        <input
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          placeholder={placeholder ?? t('common.form.phonePlaceholder')}
          /* The NATIONAL part only. The code lives on the button beside it, and
             a field that repeated it would let the two disagree. */
          value={parsed.national}
          onChange={(event) => handleNumber(event.target.value)}
          className={cn(
            'text-text-body min-w-0 flex-1 border-0 bg-transparent p-0',
            'font-sans text-[13px] font-normal outline-none',
            'placeholder:text-text-faint',
            'disabled:cursor-not-allowed',
          )}
        />
      </div>

      <ComboboxContent className="w-[280px]">
        <ComboboxSearch placeholder={t('common.form.phoneSearch')} />

        <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
          {t('common.form.noMatches')}
        </ComboboxEmpty>

        <ComboboxList>
          {(code: string) => {
            const option = byCode.get(code)
            if (!option) return null
            return (
              <ComboboxItem key={code} value={code}>
                <CircleFlag code={code} size={18} />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {/* The dial code, muted — the row's secondary reading, and the
                    thing the user is actually choosing. */}
                <span className="text-text-muted shrink-0 text-[12px]">
                  {option.hint}
                </span>
              </ComboboxItem>
            )
          }}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
